import { useEffect, useRef, useState } from 'react';
import type { AssistJoin } from '@360face/domain';

/** Branded background behind the operator (owner 21.10.2026), same look as the kiosk welcome screen. */
export const OPERATOR_BACKGROUND = '/operator-bg.jpg';

/** Daily allows one call frame per page: a new frame starts only after the previous one is destroyed. */
let released: Promise<unknown> = Promise.resolve();

/**
 * The operator's side of the video call. Uses Daily's library so the operator's camera gets the
 * 360°FACE background (owner 21.10.2026: the customer must see where the operator talks from).
 * Browsers without video processing get the call without the background; if the library fails,
 * the plain call frame is shown.
 */
export function OperatorVideo({ join, title }: { join: AssistJoin; title: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [plain, setPlain] = useState(false);
  const { roomUrl, token } = join;

  useEffect(() => {
    const el = box.current;
    if (!el || plain) return;
    let gone = false;
    let destroy: (() => Promise<void>) | undefined;
    const run = released.then(async () => {
      if (gone) return;
      const { default: Daily } = await import('@daily-co/daily-js');
      if (gone) return;
      const frame = Daily.createFrame(el, {
        showLeaveButton: false,
        iframeStyle: { width: '100%', height: '100%', border: '0' },
      });
      destroy = () => frame.destroy();
      let background: ArrayBuffer | undefined;
      if (Daily.supportedBrowser().supportsVideoProcessing) {
        // Our own file, passed as bytes: the Daily frame never fetches from hub.360face.pt.
        background = await fetch(OPERATOR_BACKGROUND)
          .then((r) => (r.ok ? r.arrayBuffer() : undefined))
          .catch(() => undefined);
      }
      if (gone) return;
      await frame.join({
        url: roomUrl,
        token,
        ...(background
          ? { inputSettings: { video: { processor: { type: 'background-image', config: { source: background } } } } }
          : {}),
      });
    });
    run.catch(() => {
      if (!gone) setPlain(true);
    });
    return () => {
      gone = true;
      released = run.catch(() => undefined).then(() => destroy?.().catch(() => undefined));
    };
  }, [roomUrl, token, plain]);

  if (plain) {
    return (
      <iframe title={title} src={`${roomUrl}?t=${encodeURIComponent(token)}`} allow="camera; microphone; autoplay" />
    );
  }
  return <div ref={box} className="op-video-frame" data-testid="operator-video" />;
}
